import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AuthModal from '../components/AuthModal';
import { API_URL } from '../config/api';
import YCodeThumbnail from '../components/YCodeThumbnail';
import './QuestionPackageDetail.css';

const QuestionPackageDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  // GST calculation helper
  const GST_RATE = 0.18; // 18%
  const calculateGST = (price) => {
    return parseFloat((price * GST_RATE).toFixed(2));
  };
  const calculateTotal = (price) => {
    return parseFloat((price + calculateGST(price)).toFixed(2));
  };
  const { isAuthenticated, isAdmin: checkIsAdmin, user } = useAuth();
  const [packageData, setPackageData] = useState(null);
  const [sections, setSections] = useState([]);
  const [questions, setQuestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [isPurchased, setIsPurchased] = useState(false);
  const [checkingPurchase, setCheckingPurchase] = useState(true);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [selectedDifficultyTab, setSelectedDifficultyTab] = useState({}); // { sectionId: 'ALL' | 'EASY' | 'MEDIUM' | 'HARD' }
  
  // Check if user is admin - admins can see all content
  const isAdmin = checkIsAdmin() || (user && user.role === 'ADMIN');

  useEffect(() => {
    fetchPackageDetails();
    checkPurchaseStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (showPurchaseModal || showLoginModal || showAuthModal) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.documentElement.style.overflow = 'unset';
    };
  }, [showPurchaseModal, showLoginModal, showAuthModal]);

  const checkPurchaseStatus = async () => {
    if (!isAuthenticated()) {
      setCheckingPurchase(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/question-packages/${id}/check-purchase`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        credentials: 'include'
      });
      
      const data = await response.json();
      setIsPurchased(data.purchased);
    } catch (error) {
      console.error('Error checking purchase:', error);
    } finally {
      setCheckingPurchase(false);
    }
  };

  const fetchPackageDetails = async () => {
    try {
      // Fetch package details with auth header to get purchase status
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const packageResponse = await fetch(`${API_URL}/api/question-packages/${id}`, {
        headers: headers
      });
      if (packageResponse.ok) {
        const packageData = await packageResponse.json();
        console.log(`Package ${id}: Package data loaded:`, {
          name: packageData.name,
          totalQuestions: packageData.totalQuestions,
          sectionsCount: packageData.sections?.length || 0
        });
        setPackageData(packageData);
        // If package data indicates it's a preview (isPreview: true), user hasn't purchased
        if (packageData.isPreview) {
          setIsPurchased(false);
        }
      } else {
        setError('Package not found');
        setLoading(false);
        return;
      }

      // Fetch package sections
      const sectionsResponse = await fetch(`${API_URL}/api/question-sections/package/${id}`);
      if (sectionsResponse.ok) {
        const sectionsData = await sectionsResponse.json();
        setSections(sectionsData);
        
        // Helper function to sort questions by questionOrder (with ID as tiebreaker)
        const sortQuestions = (questions) => {
          if (!Array.isArray(questions)) return [];
          return [...questions].sort((a, b) => {
            const orderA = a.questionOrder || 999;
            const orderB = b.questionOrder || 999;
            // Primary sort by questionOrder
            if (orderA !== orderB) {
              return orderA - orderB;
            }
            // Secondary sort by ID (creation order) for stable sorting
            return (a.id || 0) - (b.id || 0);
          });
        };

        // Fetch ALL questions for the package in ONE request (optimized!)
        // Questions are now publicly visible, but access control is handled by backend
        try {
          const token = localStorage.getItem('token');
          const headers = {};
          if (token) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          const allQuestionsResponse = await fetch(`${API_URL}/api/questions/package/${id}`, {
            headers: headers
          });
          
          if (allQuestionsResponse.ok) {
            // Response format: { questions: { sectionId: [...] }, hasAccess: boolean, firstSectionId: number }
            const responseData = await allQuestionsResponse.json();
            console.log(`Package ${id}: Received questions data:`, responseData);
            
            // Handle both old format (direct questions object) and new format (with metadata)
            const questionsData = responseData.questions || responseData;
            const hasAccess = responseData.hasAccess || false;
            const firstSectionId = responseData.firstSectionId || null;
            
            // Sort questions within each section by questionOrder
            // Convert sectionId keys to numbers to match section.id type
            const sortedQuestionsData = {};
            let totalCount = 0;
            for (const sectionId in questionsData) {
              const sectionQuestions = questionsData[sectionId];
              if (Array.isArray(sectionQuestions)) {
                const numericSectionId = Number(sectionId); // Convert string key to number
                sortedQuestionsData[numericSectionId] = sortQuestions(sectionQuestions);
                totalCount += sectionQuestions.length;
                console.log(`Package ${id}: Section ${sectionId} has ${sectionQuestions.length} questions`);
              }
            }
            console.log(`Package ${id}: Total questions found: ${totalCount}, hasAccess: ${hasAccess}, firstSectionId: ${firstSectionId}`);
            setQuestions(sortedQuestionsData);
          } else if (allQuestionsResponse.status === 403) {
            // This should not happen anymore as questions are now publicly visible
            // But keep this for backward compatibility
            const errorData = await allQuestionsResponse.json().catch(() => ({}));
            console.warn('Access denied to questions:', errorData.message || 'You must purchase this package to access questions');
            setQuestions({}); // Set empty questions - user will see purchase prompt
          } else {
            const errorText = await allQuestionsResponse.text();
            console.error(`Package ${id}: Error fetching questions - Status: ${allQuestionsResponse.status}, Response:`, errorText);
            // Fallback: Fetch lightweight questions for each section in parallel (only if not access denied)
            if (allQuestionsResponse.status !== 403) {
              const questionPromises = sectionsData.map(async (section) => {
                try {
                  const questionsResponse = await fetch(`${API_URL}/api/questions/section/${section.id}/basic`, {
                    headers: {
                      'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                  });
                  if (questionsResponse.ok) {
                    const sectionQuestions = await questionsResponse.json();
                    return { sectionId: section.id, questions: sortQuestions(sectionQuestions) };
                  } else if (questionsResponse.status === 403) {
                    // Access denied for this section too
                    return { sectionId: section.id, questions: [] };
                  }
                  return { sectionId: section.id, questions: [] };
                } catch (error) {
                  console.error(`Error fetching questions for section ${section.id}:`, error);
                  return { sectionId: section.id, questions: [] };
                }
              });
              
              const results = await Promise.all(questionPromises);
              const questionsData = {};
              results.forEach(result => {
                questionsData[result.sectionId] = result.questions;
              });
              setQuestions(questionsData);
            } else {
              setQuestions({});
            }
          }
        } catch (error) {
          console.error(`Package ${id}: Error fetching questions:`, error);
          setQuestions({});
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching package details:', error);
      setError('Unable to load package details');
      setLoading(false);
    }
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const getDifficultyColor = (difficulty) => {
    const colors = {
      'BEGINNER': 'from-green-500 to-green-600',
      'INTERMEDIATE': 'from-blue-500 to-blue-600',
      'ADVANCED': 'from-orange-500 to-orange-600',
      'EXPERT': 'from-red-500 to-red-600'
    };
    return colors[difficulty] || 'from-gray-500 to-gray-600';
  };

  const getDifficultyBadge = (difficulty) => {
    const gradient = getDifficultyColor(difficulty);
    return (
      <span className={`px-4 py-2 rounded-full text-sm font-bold text-white bg-gradient-to-r ${gradient} shadow-lg`}>
        {difficulty}
      </span>
    );
  };

  const handleStartPractice = async () => {
    if (!isAuthenticated()) {
      alert('Please login to start practice');
      navigate('/login');
      return;
    }
    
    if (!isPurchased && !isAdmin) {
      alert('Please purchase this package to start practice');
      setShowPurchaseModal(true);
      return;
    }
    
    // Navigate to first question
    if (sections.length > 0 && questions[sections[0].id]?.length > 0) {
      const firstSection = sections[0];
      const firstQuestion = questions[firstSection.id][0];
      navigate(`/question-ide?questionId=${firstQuestion.id}&packageId=${id}&sectionId=${firstSection.id}`);
    } else {
      alert('No questions available in this package yet');
    }
  };

  const handlePurchaseClick = () => {
    if (!isAuthenticated()) {
      setShowLoginModal(true);
      return;
    }
    setShowPurchaseModal(true);
  };

  const handlePurchase = async () => {
    try {
      setShowPurchaseModal(false);
      
      // Check if user is admin - admins get free access
      if (isAdmin) {
        // Direct enrollment for admin users
        try {
          const enrollResponse = await fetch(`${API_URL}/api/question-packages/${packageData.id}/purchase`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            },
            credentials: 'include'
          });
          
          if (enrollResponse.ok) {
            setPaymentDetails({
              packageName: packageData.name,
              transactionId: 'ADMIN_FREE_ACCESS',
              orderId: 'ADMIN_' + Date.now(),
              amount: 0
            });
            setShowPaymentSuccess(true);
            setIsPurchased(true);
            checkPurchaseStatus();
            return;
          }
        } catch (error) {
          console.error('Admin enrollment error:', error);
        }
      }
      
      // Step 1: Create Razorpay order for regular users
      const orderResponse = await fetch(`${API_URL}/api/question-packages/${packageData.id}/payment/create-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include'
      });
      
      if (!orderResponse.ok) {
        const errorData = await orderResponse.json();
        alert(errorData.error || 'Failed to create order. Please try again.');
        return;
      }
      
      const orderData = await orderResponse.json();
      
      // Step 2: Open Razorpay checkout
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'YCode Universe',
        description: orderData.packageName,
        order_id: orderData.orderId,
        prefill: {
          name: localStorage.getItem('username') || '',
          email: localStorage.getItem('email') || '',
        },
        theme: {
          color: '#8b5cf6'
        },
        handler: async function (response) {
          // Step 3: Verify payment on backend
          try {
            const verifyResponse = await fetch(`${API_URL}/api/question-packages/${packageData.id}/payment/verify`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify({
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature
              })
            });
            
            if (verifyResponse.ok) {
              await verifyResponse.json(); // Consume response
              
              // Show beautiful success modal
              setPaymentDetails({
                packageName: packageData.name,
                transactionId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                amount: calculateTotal(packageData.price)
              });
              setShowPaymentSuccess(true);
              setIsPurchased(true);
              
              // Refresh package details to update enrollment status
              checkPurchaseStatus();
            } else {
              const errorData = await verifyResponse.json();
              alert(`Payment verification failed: ${errorData.error}\nPlease contact support with Order ID: ${response.razorpay_order_id}`);
            }
          } catch (error) {
            console.error('Payment verification error:', error);
            alert('Payment completed but verification failed. Please contact support.');
          }
        },
        modal: {
          ondismiss: function() {
            alert('Payment cancelled. You can try again anytime.');
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      razorpay.open();
      
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to initiate payment. Please try again.');
    }
  };

  if (loading || checkingPurchase) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center pt-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-500 mx-auto mb-4"></div>
          <div className="text-white text-xl">Loading package details...</div>
        </div>
      </div>
    );
  }

  if (error || !packageData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center pt-20">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="text-6xl mb-6">😕</div>
          <h2 className="text-3xl font-bold text-white mb-4">Oops!</h2>
          <p className="text-red-400 text-xl mb-6">{error || 'Package not found'}</p>
          <Link
            to="/question-packages"
            className="inline-block bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 shadow-lg"
          >
            ← Back to Packages
          </Link>
        </div>
      </div>
    );
  }

  // Use packageData.totalQuestions if available (for unpurchased packages), 
  // otherwise calculate from loaded questions (for purchased packages)
  const totalQuestions = packageData?.totalQuestions != null && packageData.totalQuestions > 0
    ? packageData.totalQuestions
    : Object.values(questions).reduce((sum, sectionQuestions) => sum + sectionQuestions.length, 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 pt-20">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        <div className="flex items-center gap-2 text-sm mb-8">
          <Link to="/" className="text-gray-400 hover:text-purple-400 transition-colors">Home</Link>
          <span className="text-gray-600">›</span>
          <Link to="/question-packages" className="text-gray-400 hover:text-purple-400 transition-colors">Question Packages</Link>
          <span className="text-gray-600">›</span>
          <span className="text-white font-semibold">{packageData.name}</span>
        </div>
      </div>

      {/* Hero Section - Package Overview */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-8 md:p-12 border border-white/20 shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left - Package Image/Icon */}
            <div className="lg:col-span-1">
              {packageData.imageUrl ? (
                <img 
                  src={packageData.imageUrl} 
                  alt={packageData.name} 
                  className="w-full h-80 object-cover rounded-2xl shadow-2xl"
                />
              ) : (
                <div className="w-full h-80 bg-gradient-to-br from-purple-500 via-pink-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-transform duration-300">
                  <span className="text-9xl animate-bounce-slow">📦</span>
                </div>
              )}
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 text-center border border-white/20">
                  <div className="text-3xl font-bold text-purple-400">{totalQuestions}</div>
                  <div className="text-sm text-gray-300 mt-1">Questions</div>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 text-center border border-white/20">
                  <div className="text-3xl font-bold text-pink-400">{sections.length}</div>
                  <div className="text-sm text-gray-300 mt-1">Sections</div>
                </div>
              </div>
            </div>
            
            {/* Right - Package Details */}
            <div className="lg:col-span-2">
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-4 mb-6">
                {getDifficultyBadge(packageData.difficultyLevel)}
                <span className="px-4 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30 rounded-full text-purple-300 text-sm font-semibold">
                  📚 {packageData.category}
                </span>
                {isAdmin && (
                  <span className="px-4 py-2 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30 rounded-full text-purple-300 text-sm font-semibold">
                    👑 Admin Access
                  </span>
                )}
                {isPurchased && !isAdmin && (
                  <span className="px-4 py-2 bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-400/30 rounded-full text-green-300 text-sm font-semibold animate-pulse-slow">
                    ✅ Purchased
                  </span>
                )}
              </div>
              
              {/* Title */}
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                {packageData.name}
              </h1>
              
              {/* Description */}
              <p className="text-gray-300 text-lg md:text-xl mb-8 leading-relaxed">
                {packageData.description}
              </p>
              
              {/* Meta Info */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-8">
                <div className="flex items-center gap-3 bg-white/5 rounded-lg p-4 border border-white/10">
                  <span className="text-2xl">⏱️</span>
                  <div>
                    <div className="text-white font-bold text-lg">{packageData.estimatedDurationHours}h</div>
                    <div className="text-gray-400 text-xs">Duration</div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 bg-white/5 rounded-lg p-4 border border-white/10">
                  <span className="text-2xl">💰</span>
                  <div className="flex-1 w-full sm:w-auto">
                    {packageData.isOnSale && packageData.originalPrice ? (
                      <>
                        <div className="flex items-center gap-2 mb-2 sm:mb-1">
                          <span className="text-gray-500 line-through text-xs sm:text-sm">₹{packageData.originalPrice}</span>
                          {packageData.discountPercentage && (
                            <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white px-2 py-0.5 rounded text-xs font-bold">
                              {packageData.discountPercentage}% OFF
                            </span>
                          )}
                        </div>
                        <div className="space-y-2 sm:space-y-1.5 pt-2 border-t border-white/10">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-300 text-xs sm:text-sm">Price:</span>
                            <span className="text-white text-sm sm:text-base font-semibold">₹{packageData.price}</span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-gray-400 text-xs">GST (18%):</span>
                            <span className="text-gray-400 text-xs sm:text-sm">₹{calculateGST(packageData.price)}</span>
                          </div>
                          <div className="flex justify-between items-center pt-2 sm:pt-1.5 border-t border-white/10">
                            <span className="text-white text-sm sm:text-base font-semibold">Total:</span>
                            <span className="text-green-400 font-bold text-lg sm:text-xl">₹{calculateTotal(packageData.price)}</span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2 sm:space-y-1.5 pt-2 border-t border-white/10">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-300 text-xs sm:text-sm">Price:</span>
                          <span className="text-white text-sm sm:text-base font-semibold">₹{packageData.price}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-xs">GST (18%):</span>
                          <span className="text-gray-400 text-xs sm:text-sm">₹{calculateGST(packageData.price)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 sm:pt-1.5 border-t border-white/10">
                          <span className="text-white text-sm sm:text-base font-semibold">Total:</span>
                          <span className="text-green-400 font-bold text-lg sm:text-xl">₹{calculateTotal(packageData.price)}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-white/5 rounded-lg p-4 border border-white/10">
                  <span className="text-2xl">🎯</span>
                  <div>
                    <div className="text-white font-bold text-lg">{totalQuestions}</div>
                    <div className="text-gray-400 text-xs">Challenges</div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4">
                {(isPurchased || isAdmin) ? (
                  <button
                    onClick={handleStartPractice}
                    className="flex-1 min-w-[200px] bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-2xl hover:shadow-purple-500/50 transform hover:scale-105 flex items-center justify-center gap-3"
                  >
                    <span className="text-2xl">🚀</span>
                    Start Practicing
                  </button>
                ) : (
                  <>
                    <button 
                      onClick={handlePurchaseClick}
                      className="flex-1 min-w-[200px] bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 shadow-2xl hover:shadow-green-500/50 transform hover:scale-105 flex items-center justify-center gap-3"
                    >
                      <span className="text-2xl">🛒</span>
                      Purchase Package
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Package Sections - Accordion Style */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="mb-8">
          <h2 className="text-4xl font-bold text-white mb-4 flex items-center gap-3">
            <span className="text-purple-400">💻</span>
            Practice Problems
          </h2>
          <p className="text-gray-400 text-lg">
            {sections.length} sections • {totalQuestions} questions • {packageData.estimatedDurationHours} hours total
          </p>
        </div>
        
        {sections.length === 0 ? (
          <div className="bg-white/10 backdrop-blur-md rounded-2xl p-12 text-center border border-white/20">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-2xl font-bold text-white mb-2">No Sections Available Yet</h3>
            <p className="text-gray-300">
              Course content is being prepared. Check back soon!
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {sections.map((section, index) => {
              // Ensure we use the correct key type (number) to match questions object
              const sectionQuestions = questions[section.id] || questions[String(section.id)] || [];
              const isExpanded = expandedSections.has(section.id);
              const isFirstSection = index === 0;
              // Section is locked if: admin-locked OR (not admin/not purchased AND not first section)
              const isAdminLocked = section.isLocked === true;
              const isPurchaseLocked = !isAdmin && !isPurchased && !isFirstSection;
              const isLocked = isAdminLocked || isPurchaseLocked;
              
              // If user hasn't purchased and questions aren't loaded, show section.questionCount if available
              const displayQuestionCount = sectionQuestions.length > 0 
                ? sectionQuestions.length 
                : (section.questionCount || 0);
              
              return (
                <div 
                  key={section.id} 
                  className={`bg-white/10 backdrop-blur-md rounded-2xl border overflow-hidden transition-all duration-300 ${isLocked ? 'border-orange-400/30 opacity-75' : 'border-white/20 hover:border-purple-400/50 hover:shadow-xl'}`}
                >
                  {/* Section Header - Clickable */}
                  <button
                    onClick={() => isLocked ? handlePurchaseClick() : toggleSection(section.id)}
                    className="w-full p-6 flex items-center justify-between hover:bg-white/5 transition-colors duration-200"
                  >
                    <div className="flex items-center gap-4 flex-1 text-left">
                      {/* Section Number */}
                      <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg flex-shrink-0">
                        {index + 1}
                      </div>
                      
                      {/* Section Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl md:text-2xl font-bold text-white">
                            {section.title}
                          </h3>
                          {(isLocked || section.isLocked) && (
                            <span className="px-3 py-1 bg-orange-500/20 border border-orange-400/50 rounded-full text-orange-300 text-xs font-bold flex items-center gap-1 animate-pulse-slow">
                              🔒 Locked
                            </span>
                          )}
                        </div>
                        {section.topic && (
                          <p className="text-purple-400 text-sm mb-1 font-semibold">
                            📌 {section.topic}
                          </p>
                        )}
                        {section.description && (
                          <p className="text-gray-300 text-sm">
                            {section.description}
                          </p>
                        )}
                      </div>
                      
                      {/* Section Stats */}
                      <div className="hidden md:flex items-center gap-6 mr-4">
                        <div className="text-center">
                          <div className="text-2xl font-bold text-white">{displayQuestionCount}</div>
                          <div className="text-xs text-gray-400">Questions</div>
                        </div>
                        {section.estimatedDurationMinutes && (
                          <div className="text-center">
                            <div className="text-2xl font-bold text-purple-400">{section.estimatedDurationMinutes}m</div>
                            <div className="text-xs text-gray-400">Duration</div>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Expand/Collapse Icon */}
                    <div className={`text-3xl transform transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''} ${isLocked ? 'text-orange-400' : 'text-purple-400'}`}>
                      {isLocked ? '🔒' : '▼'}
                    </div>
                  </button>
                  
                  {/* Section Content - Questions */}
                  {isExpanded && !isLocked && !section.isLocked && (() => {
                    // Count questions by difficulty
                    const easyCount = sectionQuestions.filter(q => q.difficultyLevel === 'EASY').length;
                    const mediumCount = sectionQuestions.filter(q => q.difficultyLevel === 'MEDIUM').length;
                    const hardCount = sectionQuestions.filter(q => q.difficultyLevel === 'HARD').length;
                    
                    // Get current tab for this section (default to first available: EASY > MEDIUM > HARD)
                    const defaultTab = easyCount > 0 ? 'EASY' : mediumCount > 0 ? 'MEDIUM' : 'HARD';
                    const currentTab = selectedDifficultyTab[section.id] || defaultTab;
                    
                    // Filter questions based on selected tab
                    const filteredQuestions = sectionQuestions.filter(q => q.difficultyLevel === currentTab);
                    
                    return (
                      <div className="border-t border-white/10 bg-gradient-to-b from-gray-900/50 to-gray-900/30 animate-slide-down">
                        {sectionQuestions.length === 0 ? (
                          <div className="text-center py-12 px-6">
                            <div className="text-4xl mb-3 opacity-50">📝</div>
                            {!isPurchased && !isAdmin ? (
                              <>
                                <p className="text-gray-400 text-sm mb-4">
                                  Questions are available in this section. Purchase the package to unlock and access all questions.
                                </p>
                                <button
                                  onClick={handlePurchaseClick}
                                  className="mt-4 px-6 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
                                >
                                  Purchase Package to Unlock Questions
                                </button>
                              </>
                            ) : (
                              <p className="text-gray-400 text-sm">No questions available yet</p>
                            )}
                          </div>
                        ) : (
                          <>
                            {/* Difficulty Tabs */}
                            <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 overflow-x-auto bg-gray-900/30">
                              {easyCount > 0 && (
                                <button
                                  onClick={() => setSelectedDifficultyTab(prev => ({ ...prev, [section.id]: 'EASY' }))}
                                  className={`px-5 py-2.5 rounded-xl text-base font-bold transition-all duration-200 flex items-center gap-3 whitespace-nowrap shadow-sm ${
                                    currentTab === 'EASY'
                                      ? 'bg-emerald-500/30 text-emerald-300 border-2 border-emerald-400/60 shadow-emerald-500/20'
                                      : 'bg-white/5 text-gray-400 hover:bg-emerald-500/15 hover:text-emerald-300 border-2 border-transparent'
                                  }`}
                                >
                                  Easy
                                  <span className="bg-emerald-500/30 px-2.5 py-1 rounded-full text-sm font-bold text-emerald-300">{easyCount}</span>
                                </button>
                              )}
                              {mediumCount > 0 && (
                                <button
                                  onClick={() => setSelectedDifficultyTab(prev => ({ ...prev, [section.id]: 'MEDIUM' }))}
                                  className={`px-5 py-2.5 rounded-xl text-base font-bold transition-all duration-200 flex items-center gap-3 whitespace-nowrap shadow-sm ${
                                    currentTab === 'MEDIUM'
                                      ? 'bg-amber-500/30 text-amber-300 border-2 border-amber-400/60 shadow-amber-500/20'
                                      : 'bg-white/5 text-gray-400 hover:bg-amber-500/15 hover:text-amber-300 border-2 border-transparent'
                                  }`}
                                >
                                  Medium
                                  <span className="bg-amber-500/30 px-2.5 py-1 rounded-full text-sm font-bold text-amber-300">{mediumCount}</span>
                                </button>
                              )}
                              {hardCount > 0 && (
                                <button
                                  onClick={() => setSelectedDifficultyTab(prev => ({ ...prev, [section.id]: 'HARD' }))}
                                  className={`px-5 py-2.5 rounded-xl text-base font-bold transition-all duration-200 flex items-center gap-3 whitespace-nowrap shadow-sm ${
                                    currentTab === 'HARD'
                                      ? 'bg-rose-500/30 text-rose-300 border-2 border-rose-400/60 shadow-rose-500/20'
                                      : 'bg-white/5 text-gray-400 hover:bg-rose-500/15 hover:text-rose-300 border-2 border-transparent'
                                  }`}
                                >
                                  Hard
                                  <span className="bg-rose-500/30 px-2.5 py-1 rounded-full text-sm font-bold text-rose-300">{hardCount}</span>
                                </button>
                              )}
                            </div>
                            
                            {/* Questions List */}
                            <div className="questions-scroll-container max-h-[500px] overflow-y-auto scroll-smooth">
                              {filteredQuestions.length === 0 ? (
                                <div className="text-center py-8 px-6">
                                  <p className="text-gray-500 text-sm">No questions available</p>
                                </div>
                              ) : (
                                <div className="divide-y divide-white/5">
                                  {filteredQuestions.map((question, qIndex) => {
                                    // Check if question is clickable
                                    // Question is accessible if: admin OR purchased OR (first section - all questions are accessible)
                                    // Question is locked if: admin-locked OR (not admin/not purchased AND not first section)
                                    const isQuestionAdminLocked = question.isLocked === true;
                                    const isFirstSection = index === 0;
                                    // All questions in first section are accessible, not just easy ones
                                    const canClickQuestion = !isQuestionAdminLocked && (isAdmin || isPurchased || isFirstSection);
                                    
                                    const handleQuestionClick = (e) => {
                                      if (!canClickQuestion) {
                                        e.preventDefault();
                                        handlePurchaseClick();
                                        return;
                                      }
                                      // Allow default Link behavior
                                    };
                                    
                                    return (
                                    <Link 
                                      key={question.id} 
                                      to={canClickQuestion ? `/question-ide?questionId=${question.id}&packageId=${id}&sectionId=${section.id}` : '#'}
                                      onClick={handleQuestionClick}
                                      className={`question-row group flex items-center gap-4 px-6 py-4 hover:bg-white/5 transition-all duration-200 ${
                                        canClickQuestion ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'
                                      }`}
                                    >
                                      {/* Question Number */}
                                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 transition-all duration-200 ${
                                        question.difficultyLevel === 'EASY' 
                                          ? 'bg-emerald-500/20 text-emerald-400 group-hover:bg-emerald-500/30' 
                                          : question.difficultyLevel === 'MEDIUM' 
                                          ? 'bg-amber-500/20 text-amber-400 group-hover:bg-amber-500/30'
                                          : 'bg-rose-500/20 text-rose-400 group-hover:bg-rose-500/30'
                                      }`}>
                                        {qIndex + 1}
                                      </div>
                                      
                                      {/* Question Title */}
                                      <div className="flex-1 min-w-0">
                                        <h5 className="text-white font-semibold text-base group-hover:text-purple-300 transition-colors truncate">
                                          {question.title}
                                        </h5>
                                        {question.submissionsCount > 0 && (
                                          <span className="text-xs text-gray-500">
                                            {question.submissionsCount} submissions
                                          </span>
                                        )}
                                      </div>
                                      
                                      {/* Success Rate */}
                                      {question.successRate !== undefined && question.successRate !== null && (
                                        <div className={`text-right flex-shrink-0 ${
                                          question.successRate >= 70 
                                            ? 'text-emerald-400' 
                                            : question.successRate >= 40 
                                            ? 'text-amber-400'
                                            : question.successRate > 0
                                            ? 'text-rose-400'
                                            : 'text-gray-500'
                                        }`}>
                                          <div className="text-sm font-bold">{Number(question.successRate).toFixed(1)}%</div>
                                          <div className="text-xs opacity-60">success</div>
                                        </div>
                                      )}
                                      
                                      {/* Arrow or Lock Icon */}
                                      {!canClickQuestion && (
                                        <div className="text-orange-400 flex-shrink-0" title="Question is locked">
                                          🔒
                                        </div>
                                      )}
                                      {canClickQuestion && (
                                        <div className="text-gray-600 group-hover:text-purple-400 transition-colors flex-shrink-0">
                                          <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                          </svg>
                                        </div>
                                      )}
                                    </Link>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CTA Section - Hide for admins and purchased users */}
      {!isPurchased && !isAdmin && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="bg-gradient-to-r from-purple-900/30 via-pink-900/30 to-purple-900/30 backdrop-blur-xl rounded-3xl p-12 text-center border border-purple-400/30 shadow-2xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 via-pink-500/10 to-purple-500/10 animate-gradient-x"></div>
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Ready to Master These Challenges?
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Get lifetime access to all {totalQuestions} questions across {sections.length} sections for just ₹{calculateTotal(packageData.price)} (incl. GST)
              </p>
              <button
                onClick={handlePurchaseClick}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-12 py-5 rounded-xl text-xl font-bold transition-all duration-300 shadow-2xl hover:shadow-green-500/50 transform hover:scale-110 inline-flex items-center gap-3"
              >
                <span className="text-3xl">🛒</span>
                Purchase Now - ₹{calculateTotal(packageData.price)}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-black/30 border-t border-white/10 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center justify-center md:justify-start mb-4">
                <YCodeThumbnail size="medium" />
              </div>
              <p className="text-gray-400 text-sm text-center md:text-left">
                Advanced coding platform for modern learners
              </p>
              <p className="text-orange-400 text-xs mt-2 text-center md:text-left">
                🐛 Debugging the Road to Progress
              </p>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Learn</h3>
              <ul className="space-y-2">
                <li><Link to="/courses" className="text-gray-400 hover:text-purple-400 transition-colors text-sm">Courses</Link></li>
                <li><Link to="/question-packages" className="text-gray-400 hover:text-purple-400 transition-colors text-sm">Practice</Link></li>
                <li><Link to="/articles" className="text-gray-400 hover:text-purple-400 transition-colors text-sm">Articles</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Tools</h3>
              <ul className="space-y-2">
                <li><Link to="/ide" className="text-gray-400 hover:text-purple-400 transition-colors text-sm">Online IDE</Link></li>
                <li><Link to="/ai-assistant" className="text-gray-400 hover:text-purple-400 transition-colors text-sm">AI Assistant</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-white font-bold mb-4">Company</h3>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-purple-400 transition-colors text-sm">About Us</Link></li>
                <li><Link to="/" className="text-gray-400 hover:text-purple-400 transition-colors text-sm">Home</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-gray-400 text-sm">
              © 2024 YCode. All rights reserved. Made with ❤️ for developers.
            </p>
          </div>
        </div>
      </footer>

      {/* Login Required Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[200] p-4 animate-fade-in" onClick={() => setShowLoginModal(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border-2 border-orange-400/50 shadow-2xl shadow-orange-500/30" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3">🔒</div>
              <h3 className="text-2xl font-bold text-white mb-2">Login Required</h3>
              <p className="text-gray-300 text-sm mb-4">
                You need to be logged in to purchase this package. Please login or create an account to continue.
              </p>
            </div>

            <div className="bg-orange-900/30 border border-orange-400/30 rounded-lg p-3 mb-4">
              <div className="flex items-start gap-2">
                <span className="text-2xl">ℹ️</span>
                <div>
                  <p className="text-orange-300 text-xs font-bold mb-1">Why Login?</p>
                  <p className="text-gray-300 text-xs">Logging in allows us to track your progress, save your work, and unlock all questions in this package.</p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowLoginModal(false);
                  setShowAuthModal(true);
                }}
                className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white px-4 py-3 rounded-lg font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-orange-500/50 transform hover:scale-105"
              >
                🚀 Go to Login
              </button>
              <button
                onClick={() => setShowLoginModal(false)}
                className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold text-sm transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && packageData && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[200] p-4 animate-fade-in" onClick={() => setShowPurchaseModal(false)}>
          <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 max-w-md w-full border-2 border-purple-400/50 shadow-2xl shadow-purple-500/30 transform animate-scale-in max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="text-center mb-4">
              <div className="text-5xl mb-3 animate-bounce-slow">🛒</div>
              <h3 className="text-2xl font-bold text-white mb-1">Purchase Package</h3>
              <p className="text-gray-300 text-sm">Complete your purchase to unlock all content</p>
            </div>

            <div className="bg-white/10 rounded-xl p-4 mb-4 border border-white/20">
              <h4 className="text-lg font-bold text-white mb-3">{packageData.name}</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center py-1.5 border-b border-white/10">
                  <span className="text-gray-400 text-sm">Category:</span>
                  <span className="text-purple-400 font-semibold text-sm">{packageData.category}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-white/10">
                  <span className="text-gray-400 text-sm">Difficulty:</span>
                  <span>{getDifficultyBadge(packageData.difficultyLevel)}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-white/10">
                  <span className="text-gray-400 text-sm">Total Questions:</span>
                  <span className="text-white font-semibold text-sm">{totalQuestions}</span>
                </div>
                <div className="flex justify-between items-center py-1.5 border-b border-white/10">
                  <span className="text-gray-400 text-sm">Duration:</span>
                  <span className="text-white font-semibold text-sm">{packageData.estimatedDurationHours}h</span>
                </div>
                <div className="pt-3 mt-2 border-t-2 border-white/20">
                  {packageData.isOnSale && packageData.originalPrice ? (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">Original Price:</span>
                        <span className="text-gray-400 text-sm line-through">₹{packageData.originalPrice}</span>
                      </div>
                      {packageData.discountPercentage && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">Discount:</span>
                          <span className="text-red-400 font-bold text-sm">{packageData.discountPercentage}% OFF</span>
                        </div>
                      )}
                      <div className="space-y-2 pt-2 border-t border-white/10">
                        <div className="flex justify-between items-center">
                          <span className="text-white font-semibold text-sm">Price:</span>
                          <span className="text-white font-semibold text-sm">₹{packageData.price}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-400 text-sm">GST (18%):</span>
                          <span className="text-gray-400 text-sm">₹{calculateGST(packageData.price)}</span>
                        </div>
                        <div className="flex justify-between items-center pt-2 border-t border-white/20">
                          <span className="text-white font-semibold text-sm">Total Price:</span>
                          <span className="text-green-400 font-bold text-lg">₹{calculateTotal(packageData.price)}</span>
                        </div>
                      </div>
                      {packageData.offerDescription && (
                        <div className="bg-orange-900/30 border border-orange-500/30 rounded-lg p-2 mt-2">
                          <p className="text-orange-300 text-xs text-center">⚡ {packageData.offerDescription}</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-white font-semibold text-sm">Price:</span>
                        <span className="text-white font-semibold text-sm">₹{packageData.price}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-400 text-sm">GST (18%):</span>
                        <span className="text-gray-400 text-sm">₹{calculateGST(packageData.price)}</span>
                      </div>
                      <div className="flex justify-between items-center pt-2 border-t border-white/20">
                        <span className="text-white font-semibold text-sm">Total Price:</span>
                        <span className="text-green-400 font-bold text-lg">₹{calculateTotal(packageData.price)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handlePurchase}
                className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-4 py-3 rounded-lg font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-green-500/50 transform hover:scale-105"
              >
                ✅ Proceed to Payment
              </button>
              <button
                onClick={() => setShowPurchaseModal(false)}
                className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-bold text-sm transition-all duration-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Success Modal */}
      {showPaymentSuccess && paymentDetails && (
        <div 
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          style={{ zIndex: 10000 }}
          onClick={() => {
            setShowPaymentSuccess(false);
            navigate('/dashboard');
          }}
        >
          <div 
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md transform transition-all"
            onClick={(e) => e.stopPropagation()}
            style={{
              animation: 'slideIn 0.3s ease-out'
            }}
          >
            {/* Success Icon with Animation */}
            <div className="bg-gradient-to-br from-green-400 to-emerald-500 p-8 rounded-t-2xl text-center relative overflow-hidden">
              {/* Animated Background Circles */}
              <div className="absolute top-0 left-0 w-full h-full">
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-white/10 rounded-full animate-ping"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-white/20 rounded-full"></div>
              </div>
              
              {/* Success Icon */}
              <div className="relative z-10 mb-4">
                <div className="w-20 h-20 bg-white rounded-full mx-auto flex items-center justify-center shadow-lg">
                  <svg className="w-12 h-12 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              
              <h2 className="relative z-10 text-3xl font-bold text-white mb-2">
                🎉 Payment Successful!
              </h2>
              <p className="relative z-10 text-green-100 text-sm">
                Your purchase has been completed
              </p>
            </div>

            {/* Payment Details */}
            <div className="p-6">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-5 mb-5 space-y-3">
                <div className="flex justify-between items-start py-2 border-b border-gray-300">
                  <span className="text-gray-600 font-medium text-sm">Package</span>
                  <span className="text-gray-900 font-bold text-sm text-right max-w-[60%]">
                    {paymentDetails.packageName}
                  </span>
                </div>
                
                <div className="flex justify-between items-center py-2 border-b border-gray-300">
                  <span className="text-gray-600 font-medium text-sm">Amount Paid</span>
                  <span className="text-green-600 font-bold text-base">
                    ₹{paymentDetails.amount}
                  </span>
                </div>
                
                <div className="flex justify-between items-start py-2 border-b border-gray-300">
                  <span className="text-gray-600 font-medium text-sm">Transaction ID</span>
                  <span className="text-gray-900 font-mono text-xs text-right break-all max-w-[60%]">
                    {paymentDetails.transactionId}
                  </span>
                </div>
                
                <div className="flex justify-between items-start py-2">
                  <span className="text-gray-600 font-medium text-sm">Order ID</span>
                  <span className="text-gray-900 font-mono text-xs text-right break-all max-w-[60%]">
                    {paymentDetails.orderId}
                  </span>
                </div>
              </div>

              {/* Success Message */}
              <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 mb-5">
                <div className="flex items-start gap-3">
                  <span className="text-2xl">✅</span>
                  <div>
                    <p className="text-green-900 font-semibold text-sm mb-1">
                      You can now start practicing!
                    </p>
                    <p className="text-green-700 text-xs">
                      Access all questions and start improving your coding skills.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPaymentSuccess(false);
                    navigate('/dashboard');
                  }}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-4 py-3 rounded-lg font-bold text-sm transition-all duration-300 shadow-lg hover:shadow-green-500/50 transform hover:scale-105"
                >
                  Go to Dashboard
                </button>
                <button
                  onClick={() => {
                    setShowPaymentSuccess(false);
                    window.location.reload();
                  }}
                  className="px-4 py-3 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-semibold text-sm transition-all duration-300"
                >
                  Start Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </div>
  );
};

export default QuestionPackageDetail;

